import { create } from "@bufbuild/protobuf";
import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";
import { $createTextNode, $insertNodes } from "lexical";
import { useEffect, useRef } from "react";
import { toast } from "sonner";
import { Attachment, AttachmentSchema } from "@/types/proto/api/v1/attachment_service_pb";
import { $createImageNode } from "../nodes/ImageNode";

export function ImagePastePlugin({ onCreateAttachment }: { onCreateAttachment: (attachment: Attachment) => Promise<Attachment> }): null {
  const [editor] = useLexicalComposerContext();
  const processingRef = useRef(false);

  useEffect(() => {
    // 监听原生 paste 事件，而不是 Lexical 的 PASTE_COMMAND
    const handlePaste = async (event: ClipboardEvent) => {
      // 如果正在处理中，跳过
      if (processingRef.current) {
        event.preventDefault();
        event.stopPropagation();
        return;
      }

      // 获取粘贴板内容
      const items = event.clipboardData?.items;
      if (!items) {
        return;
      }

      // 检查是否有图片
      let imageFile: File | null = null;
      let hasNonImageContent = false;

      for (let i = 0; i < items.length; i++) {
        const item = items[i];
        if (item.type.startsWith("image/")) {
          imageFile = item.getAsFile();
        } else if (item.type.startsWith("text/") || item.kind === "string") {
          hasNonImageContent = true;
        }
      }

      // 额外检查 clipboardData 的 getData 方法
      // 有些情况下 items 数组只有图片，但 getData('text/plain') 有文字
      const textFromData = event.clipboardData?.getData("text/plain") || "";

      if (textFromData) {
        hasNonImageContent = true;
      }

      // 如果没有图片，让默认行为处理（纯文字粘贴）
      if (!imageFile) {
        return;
      }

      // 如果有图片+文字混合，需要手动处理
      if (hasNonImageContent) {
        // 阻止默认行为，我们手动处理
        event.preventDefault();
        event.stopPropagation();
        processingRef.current = true;

        try {
          // 获取文字内容 - 优先从 getData 获取，其次从 items 获取
          let textContent = event.clipboardData?.getData("text/plain") || "";

          // 如果 getData 没有内容，尝试从 items 获取
          if (!textContent) {
            for (let i = 0; i < items.length; i++) {
              const item = items[i];
              if (item.kind === "string" && item.type === "text/plain") {
                textContent = await new Promise<string>((resolve) => {
                  item.getAsString((str) => resolve(str));
                });
                break;
              }
            }
          }

          // 将图片转换为 ArrayBuffer 并上传
          const arrayBuffer = await imageFile.arrayBuffer();
          const content = new Uint8Array(arrayBuffer);

          const timestamp = Date.now();
          const extension = imageFile.type.split("/")[1] || "png";
          const defaultFilename = `image-${timestamp}.${extension}`;
          const filename = imageFile.name || defaultFilename;

          const attachment = create(AttachmentSchema, {
            name: "",
            filename: filename,
            content: content,
            externalLink: "",
            type: imageFile.type,
            size: BigInt(content.length),
          });

          toast.info("正在上传图片...", { id: "image-upload" });

          const uploadedAttachment = await onCreateAttachment(attachment);

          toast.success("图片上传成功", { id: "image-upload" });

          const attachmentName = uploadedAttachment.name;
          const attachmentFilename = uploadedAttachment.filename;

          // 插入文字和图片
          editor.update(() => {
            // 先插入文字（如果有）
            if (textContent) {
              const textNode = $createTextNode(textContent);
              $insertNodes([textNode]);
            }
            // 再插入图片
            const imageNode = $createImageNode(attachmentName, imageFile.name, undefined, undefined, attachmentFilename);
            $insertNodes([imageNode]);
          });
        } catch (error) {
          toast.error(`处理失败: ${error instanceof Error ? error.message : String(error)}`, {
            id: "image-upload",
          });
        } finally {
          processingRef.current = false;
        }

        return;
      }

      // 纯图片：阻止默认行为并处理
      event.preventDefault();
      event.stopPropagation();
      processingRef.current = true;

      try {
        // 将图片转换为 ArrayBuffer
        const arrayBuffer = await imageFile.arrayBuffer();
        const content = new Uint8Array(arrayBuffer);

        // 生成文件名
        const timestamp = Date.now();
        const extension = imageFile.type.split("/")[1] || "png";
        const defaultFilename = `image-${timestamp}.${extension}`;
        const filename = imageFile.name || defaultFilename;

        // 创建 attachment 对象
        const attachment = create(AttachmentSchema, {
          name: "",
          filename: filename,
          content: content,
          externalLink: "",
          type: imageFile.type,
          size: BigInt(content.length),
        });

        toast.info("正在上传图片...", { id: "image-upload" });

        // 上传图片
        const uploadedAttachment = await onCreateAttachment(attachment);

        toast.success("图片上传成功", { id: "image-upload" });

        const attachmentName = uploadedAttachment.name;
        const attachmentFilename = uploadedAttachment.filename;

        // 插入 ImageNode
        editor.update(() => {
          const imageNode = $createImageNode(attachmentName, imageFile.name, undefined, undefined, attachmentFilename);
          $insertNodes([imageNode]);
        });
      } catch (error) {
        toast.error(`图片上传失败: ${error instanceof Error ? error.message : String(error)}`, {
          id: "image-upload",
        });
      } finally {
        processingRef.current = false;
      }
    };

    // 获取 contentEditable 元素并监听 paste 事件
    setTimeout(() => {
      const rootElement = editor.getRootElement();
      if (rootElement) {
        rootElement.addEventListener("paste", handlePaste as unknown as EventListener);

        return () => {
          rootElement.removeEventListener("paste", handlePaste as unknown as EventListener);
        };
      }
    }, 0);

    return () => {};
  }, [editor, onCreateAttachment]);

  return null;
}

export default ImagePastePlugin;
